// SignatureModal.tsx
// Captures a representative's Part II signature by drawing only.
//
// The typed-name option was removed deliberately: a typed signature is fine for a
// purely digital submission, but anything handled in person (Exam, Collections, TAS,
// uploading a 2848 to PPS) needs a real drawn signature, and discovering that after
// the fact means going back to the client for a fresh form. Draw is the only mode.
//
// onConfirm receives { type: 'drawn', data: base64 PNG data URL }

import React, { useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';

interface Props {
  repName:   string;
  onConfirm: (sig: { type: 'drawn'; data: string }) => void;
  onClose:   () => void;
}

const CANVAS_W = 500;
const CANVAS_H = 140;

export default function SignatureModal({ repName, onConfirm, onClose }: Props) {
  const [hasDrawn, setHasDrawn] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing   = useRef(false);
  const lastPos   = useRef<{ x: number; y: number } | null>(null);

  // ── Canvas helpers ──────────────────────────────────────────────────────────
  const getCtx = () => canvasRef.current?.getContext('2d') ?? null;

  const clearCanvas = useCallback(() => {
    const ctx = getCtx();
    if (!ctx) return;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    setHasDrawn(false);
  }, []);

  function relPos(e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) {
    const rect = canvasRef.current!.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    const src = 'touches' in e ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top)  * scaleY,
    };
  }

  function startDraw(e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) {
    e.preventDefault();
    drawing.current = true;
    lastPos.current = relPos(e);
    const ctx = getCtx();
    if (!ctx) return;
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
  }

  function continueDraw(e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) {
    e.preventDefault();
    if (!drawing.current || !lastPos.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    const pos = relPos(e);
    ctx.lineWidth   = 2;
    ctx.lineCap     = 'round';
    ctx.strokeStyle = '#000000';
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setHasDrawn(true);
  }

  function stopDraw(e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) {
    e.preventDefault();
    drawing.current = false;
    lastPos.current = null;
  }

  function handleConfirm() {
    if (!hasDrawn) return;
    // Export the full canvas — aspect ratio is preserved server-side when embedding
    onConfirm({ type: 'drawn', data: canvasRef.current!.toDataURL('image/png') });
  }

  // ── Styles ──────────────────────────────────────────────────────────────────
  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
  };
  const modal: React.CSSProperties = {
    background: 'var(--surface, #1e2030)',
    border: '1px solid var(--border)',
    borderRadius: 12, padding: 24, width: 560, maxWidth: '95vw',
    boxShadow: '0 8px 40px rgba(0,0,0,.6)',
  };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Sign — Declaration of Representative</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{repName}</div>
          </div>
          <button className="btn" style={{ padding: '4px 8px' }} onClick={onClose}><X size={14} /></button>
        </div>

        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8, lineHeight: 1.5 }}>
          Sign in the box below using your mouse, trackpad, or finger. On a touchscreen or
          tablet you can sign directly with your finger or a stylus.
        </div>

        <div style={{
          borderRadius: 8, overflow: 'hidden',
          border: '1px solid var(--border)', marginBottom: 8,
        }}>
          <canvas
            ref={canvasRef}
            width={CANVAS_W}
            height={CANVAS_H}
            style={{ display: 'block', width: '100%', cursor: 'crosshair', touchAction: 'none', background: '#fff' }}
            onMouseDown={startDraw}
            onMouseMove={continueDraw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={continueDraw}
            onTouchEnd={stopDraw}
          />
        </div>
        <button
          className="btn"
          style={{ fontSize: 12, marginBottom: 20 }}
          onClick={clearCanvas}
        >
          Clear
        </button>

        {/* Why drawing is the only option */}
        <div style={{
          fontSize: 11, color: 'var(--muted)', marginBottom: 12,
          background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.18)',
          borderRadius: 6, padding: '8px 12px', lineHeight: 1.5,
        }}>
          A drawn signature is required. The IRS accepts a typed signature only for certain
          digital submissions — anything handled in person (Exam, Collections, TAS, or uploading
          to PPS) needs an actual signature, and a typed one would mean starting over.
        </div>

        {/* Legal notice */}
        <div style={{
          fontSize: 11, color: 'var(--muted)', marginBottom: 20,
          background: 'rgba(255,255,255,.03)', borderRadius: 6, padding: '8px 12px',
          lineHeight: 1.5,
        }}>
          By confirming, I declare under penalties of perjury that I am not currently suspended or
          disbarred from practice before the IRS, and that the information on this Form 2848 is true,
          correct, and complete.
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose} style={{ fontSize: 14 }}>Cancel</button>
          <button
            className="btn"
            onClick={handleConfirm}
            disabled={!hasDrawn}
            style={{
              fontSize: 14, fontWeight: 600,
              background: 'rgba(122,162,255,.15)',
              borderColor: 'rgba(122,162,255,.5)',
              color: 'var(--accent)',
              opacity: hasDrawn ? 1 : .45,
            }}
          >
            Confirm Signature
          </button>
        </div>
      </div>
    </div>
  );
}
